import React, { useState } from "react";
import axios from "axios";
import { Form, Input, Button, Alert, Card, Typography, Modal, Divider } from "antd";
import { UserOutlined, LockOutlined, KeyOutlined } from "@ant-design/icons";
import { requestPasswordReset } from "../api/auth/authApi";
import { fetchPasskeyLoginOptions, verifyPasskeyLogin } from "../api/auth/mfaApi";
import { resolveAuthenticatedHome } from "../utils/auth/authenticatedHomeRoute";
import { finishSignIn, signIn, type SignInResult } from "../utils/auth/sessionUtilities";
import { pageNavigator } from "../utils/navigation/pageNavigator";
import { ENROLMENT_PATH, RECOVERY_EXPIRED, RECOVERY_EXPIRED_MESSAGE, enterEnrolment } from "../lib/http/stepup";
import { authFailure, authFailureMessage } from "../lib/http/authErrors";
import { getPasskeyAssertion, isPasskeySupported, PasskeyCeremonyError, passkeyFailureMessage } from "../lib/webauthn/webauthn";
import { getAuthSession, type LoginMfaRequired } from "../lib/http/session";
import BrandLogo from "../components/branding/BrandLogo";
import PoweredBy from "../components/branding/PoweredBy";
import SecondFactorStep from "./login/SecondFactorStep";
import RecoveryStep from "./login/RecoveryStep";
import EnrolmentPrompt from "./login/EnrolmentPrompt";
import "./LoginPage.css";

const { Title } = Typography;

type Step =
	| { kind: "password" }
	| { kind: "second_factor"; challenge: LoginMfaRequired }
	| { kind: "recovery"; challenge: LoginMfaRequired }
	| { kind: "enrol_prompt"; graceUntil: string | null; home: string };

const STEP_TITLES: Record<Step["kind"], string> = {
	password: "Sign in to your account",
	second_factor: "Two-step verification",
	recovery: "Recover your account",
	enrol_prompt: "Set up two-step verification",
};

const LOGIN_FAILED = "Something has gone wrong, please try again";
const SESSION_ENDED = "Your session has ended. Please sign in again.";

// Why the last session ended (endedReason); a voluntary sign-out and an unknown reason say nothing.
const ENDED_MESSAGES: Record<string, string> = {
	session_idle: "You were signed out after a period of inactivity. Please sign in again.",
	session_absolute: "Your session reached its maximum length. Please sign in again.",
	refresh_invalid: SESSION_ENDED,
	session_revoked: SESSION_ENDED,
	refresh_reused: SESSION_ENDED,
	account_inactive: "Account is inactive. Please contact support.",
	[RECOVERY_EXPIRED]: RECOVERY_EXPIRED_MESSAGE,
};

const endedMessage = (reason: string | null): string => (reason && Object.prototype.hasOwnProperty.call(ENDED_MESSAGES, reason) ? ENDED_MESSAGES[reason] : "");

const passwordlessError = (error: unknown): string => {
	if (error instanceof PasskeyCeremonyError) return passkeyFailureMessage(error.reason);
	if (authFailure(error).code === "invalid_credentials") return "That passkey is not recognised here. Sign in with your username instead.";
	return authFailureMessage(error);
};

const LoginPage: React.FC = () => {
	const [loading, setLoading] = useState(false);
	const [passkeyLoading, setPasskeyLoading] = useState(false);
	const [error, setError] = useState(() => endedMessage(getAuthSession().endedReason));
	const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
	const [forgotPasswordForm] = Form.useForm();
	const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
	const [forgotPasswordMessage, setForgotPasswordMessage] = useState("");
	const [forgotPasswordError, setForgotPasswordError] = useState("");
	const [form] = Form.useForm();
	const [step, setStep] = useState<Step>({ kind: "password" });

	// Every sign-in path lands here (contract §6.10): second factor, forced enrolment, the grace prompt, or home.
	const proceed = (result: SignInResult) => {
		if (result.status === "mfa_required") {
			setStep({ kind: "second_factor", challenge: result.challenge });
			return;
		}
		if (result.status === "enrolment_required") {
			// The confined session is already in memory (finishSignIn); SPA-navigate so a hard reload can't discard it and bounce to /login.
			enterEnrolment(ENROLMENT_PATH);
			return;
		}
		const home = resolveAuthenticatedHome(result.user);
		if (result.mfa?.state === "grace") {
			setStep({ kind: "enrol_prompt", graceUntil: result.mfa.grace_until, home });
			return;
		}
		pageNavigator.go(home);
	};

	const restart = (message?: string) => {
		setStep({ kind: "password" });
		setError(message ?? "");
		form.setFieldValue("password", "");
	};

	const handleLogin = async (values: { username: string; password: string }) => {
		setLoading(true);
		setError("");

		try {
			proceed(await signIn(values.username, values.password));
		} catch (err: unknown) {
			// A proxy's HTML error page carries no §11 message; never leave the alert blank.
			setError(axios.isAxiosError(err) ? authFailureMessage(err, LOGIN_FAILED) : LOGIN_FAILED);
		} finally {
			setLoading(false);
		}
	};

	const handlePasskeySignIn = async () => {
		setPasskeyLoading(true);
		setError("");
		try {
			const { publicKey, flow_id } = await fetchPasskeyLoginOptions(null);
			const credential = await getPasskeyAssertion(publicKey);
			proceed(await finishSignIn(await verifyPasskeyLogin(flow_id, credential)));
		} catch (err) {
			setError(passwordlessError(err));
		} finally {
			setPasskeyLoading(false);
		}
	};

	const handleForgotPassword = async (values: { username: string }) => {
		setForgotPasswordLoading(true);
		setForgotPasswordError("");
		setForgotPasswordMessage("");

		try {
			const body = await requestPasswordReset(values.username);
			// identity answers every outcome with one body (contract §6.5), so never claim a link went out.
			setForgotPasswordMessage(typeof body?.message === "string" && body.message ? body.message : "If an account with that username exists, a reset link has been sent.");
			forgotPasswordForm.resetFields();

			// Close modal after 3 seconds
			setTimeout(() => {
				setIsForgotPasswordModalOpen(false);
				setForgotPasswordMessage("");
			}, 3000);
		} catch (err: any) {
			if (axios.isAxiosError(err) && err.response) {
				setForgotPasswordError(err.response.data.message || "Unable to process password reset request");
			} else {
				setForgotPasswordError("Something went wrong, please try again");
			}
		} finally {
			setForgotPasswordLoading(false);
		}
	};

	const handleForgotPasswordModal = () => {
		setIsForgotPasswordModalOpen(true);
		setForgotPasswordError("");
		setForgotPasswordMessage("");
		forgotPasswordForm.resetFields();
	};

	const handleCloseForgotPasswordModal = () => {
		setIsForgotPasswordModalOpen(false);
		setForgotPasswordError("");
		setForgotPasswordMessage("");
		forgotPasswordForm.resetFields();
	};

	return (
		<div className="login-container">
			<div className="login-backdrop" />
			<Card className="login-card" bordered={false}>
				<div className="login-logo">
					<BrandLogo height={40} />
				</div>

				<Title level={3} className="login-title">
					{STEP_TITLES[step.kind]}
				</Title>

				{step.kind === "second_factor" && (
					<SecondFactorStep
						challenge={step.challenge}
						onResult={proceed}
						onRestart={restart}
						onLostDevice={() => setStep({ kind: "recovery", challenge: step.challenge })}
					/>
				)}

				{step.kind === "recovery" && (
					<RecoveryStep
						challenge={step.challenge}
						onResult={proceed}
						onRestart={restart}
						onBack={() => setStep({ kind: "second_factor", challenge: step.challenge })}
					/>
				)}

				{step.kind === "enrol_prompt" && (
					<EnrolmentPrompt graceUntil={step.graceUntil} onSetUp={() => pageNavigator.go(ENROLMENT_PATH)} onLater={() => pageNavigator.go(step.home)} />
				)}

				{step.kind === "password" && (
					<Form form={form} name="login" onFinish={handleLogin} layout="vertical" size="large" className="login-form">
						<Form.Item name="username" rules={[{ required: true, message: "Please enter your username" }]} className="login-form-item">
							<Input data-testid="login-username" prefix={<UserOutlined />} placeholder="Username" autoComplete="username webauthn" />
						</Form.Item>

						<Form.Item name="password" rules={[{ required: true, message: "Please enter your password" }]} className="login-form-item">
							<Input.Password data-testid="login-password" prefix={<LockOutlined />} placeholder="Password" autoComplete="current-password" />
						</Form.Item>

						{error && (
							<Form.Item className="login-form-item">
								<Alert data-testid="login-error" message={error} type="error" showIcon closable onClose={() => setError("")} />
							</Form.Item>
						)}

						<Form.Item className="login-form-item">
							<Button data-testid="login-submit" type="primary" htmlType="submit" loading={loading} disabled={passkeyLoading} block size="large">
								Sign In
							</Button>
						</Form.Item>

						{isPasskeySupported() && (
							<>
								<Divider plain className="login-divider">
									or
								</Divider>
								<Form.Item className="login-form-item">
									<Button data-testid="login-passkey" icon={<KeyOutlined />} onClick={handlePasskeySignIn} loading={passkeyLoading} disabled={loading} block size="large">
										Sign in with a passkey
									</Button>
								</Form.Item>
							</>
						)}

						<Form.Item className="login-form-item">
							<Button
								type="link"
								block
								onClick={handleForgotPasswordModal}
								style={{ padding: 0, height: 'auto' }}
							>
								Forgot Password?
							</Button>
						</Form.Item>
					</Form>
				)}

				<PoweredBy style={{ display: "block", textAlign: "center", marginTop: "var(--mg-sp-7)" }} />
			</Card>

			{/* Forgot Password Modal */}
			<Modal
				title="Forgot Password"
				open={isForgotPasswordModalOpen}
				onCancel={handleCloseForgotPasswordModal}
				footer={null}
				destroyOnClose
			>
				<Form
					form={forgotPasswordForm}
					name="forgotPassword"
					onFinish={handleForgotPassword}
					layout="vertical"
					size="large"
				>
					<Form.Item
						name="username"
						rules={[{ required: true, message: "Please enter your username" }]}
					>
						<Input
							prefix={<UserOutlined />}
							placeholder="Enter your username"
							autoComplete="username"
						/>
					</Form.Item>

					{forgotPasswordError && (
						<Form.Item>
							<Alert
								message={forgotPasswordError}
								type="error"
								showIcon
								closable
								onClose={() => setForgotPasswordError("")}
							/>
						</Form.Item>
					)}

					{forgotPasswordMessage && (
						<Form.Item>
							<Alert
								message={forgotPasswordMessage}
								type="success"
								showIcon
							/>
						</Form.Item>
					)}

					<Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							loading={forgotPasswordLoading}
							block
							size="large"
						>
							Send Reset Link
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default LoginPage;
